// ─────────────────────────────────────────
// Leaderboard.js — Boards page
// ─────────────────────────────────────────
// Two main sections:
//   1. Course Boards — dropdown of courses, period tabs, top-10 + user rank
//   2. Following — holes played, lowest scores, head-to-head records

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from '../components/AvatarImg'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

const COURSE_PERIODS = [
  { key: 'daily', label: 'Today' },
  { key: 'monthly', label: 'Month' },
  { key: 'yearly', label: 'Year' },
  { key: 'alltime', label: 'All-Time' },
]

const FOLLOWING_PERIODS = [
  { key: 'monthly', label: 'Month' },
  { key: 'yearly', label: 'Year' },
  { key: 'alltime', label: 'All-Time' },
]

function periodStart(key) {
  const n = new Date()
  if (key === 'daily') return new Date(n.getFullYear(), n.getMonth(), n.getDate()).toISOString()
  if (key === 'monthly') return new Date(n.getFullYear(), n.getMonth(), 1).toISOString()
  if (key === 'yearly') return new Date(n.getFullYear(), 0, 1).toISOString()
  return null
}

function fmtDate(str) {
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function Leaderboard({ session }) {
  const isMobile = useIsMobile()
  const userId = session.user.id
  const [mainTab, setMainTab] = useState('course') // 'course' | 'following'
  const [myProfile, setMyProfile] = useState(null)

  // ── Course Boards ──────────────────────────────────
  const [courses, setCourses] = useState([])
  const [selectedCourse, setSelectedCourse] = useState('')
  const [coursePeriod, setCoursePeriod] = useState('alltime')
  const [courseRows, setCourseRows] = useState([])   // best score per user, sorted
  const [courseLoading, setCourseLoading] = useState(false)
  const [scoreMode, setScoreMode] = useState('gross') // 'gross' | 'net'

  // ── Following Boards ───────────────────────────────
  const [following, setFollowing] = useState([])  // [{id, username, avatar_emoji, avatar_url}]
  const [followingPeriod, setFollowingPeriod] = useState('alltime')
  const [holesPlayed, setHolesPlayed] = useState([])   // [{userId, username, ..., total}]
  const [lowestScores, setLowestScores] = useState([]) // [{userId, username, ..., score, date}]
  const [followingLoading, setFollowingLoading] = useState(false)

  // ── Head-to-Head ───────────────────────────────────
  const [h2hOpponents, setH2hOpponents] = useState([])  // profiles of people played against
  const [selectedOpponent, setSelectedOpponent] = useState(null)
  const [h2hRecord, setH2hRecord] = useState(null) // { wins, losses, ties, matches }
  const [h2hLoading, setH2hLoading] = useState(false)

  useEffect(() => {
    fetchProfile()
    fetchCourses()
  }, [])

  useEffect(() => {
    if (mainTab === 'following') {
      fetchFollowing()
    }
  }, [mainTab])

  useEffect(() => {
    if (selectedCourse) fetchCourseLeaderboard()
  }, [selectedCourse, coursePeriod])

  useEffect(() => {
    if (following.length > 0) fetchFollowingStats()
  }, [following, followingPeriod])

  useEffect(() => {
    if (selectedOpponent) fetchH2H(selectedOpponent.id)
  }, [selectedOpponent])

  // ── Data: Profile ──────────────────────────────────

  async function fetchProfile() {
    const { data } = await supabase.from('profiles').select('id, username, avatar_emoji, avatar_url').eq('id', userId).single()
    setMyProfile(data)
  }

  // ── Data: Courses ──────────────────────────────────

  async function fetchCourses() {
    const { data } = await supabase.from('rounds').select('course_name').not('course_name', 'is', null)
    if (!data) return
    const unique = [...new Set(data.map(r => r.course_name).filter(Boolean))].sort()
    setCourses(unique)
    if (unique.length > 0) setSelectedCourse(unique[0])
  }

  async function fetchCourseLeaderboard() {
    setCourseLoading(true)
    const start = periodStart(coursePeriod)
    let q = supabase
      .from('rounds')
      .select('*, profiles(id, username, avatar_emoji, avatar_url)')
      .ilike('course_name', selectedCourse)
      .not('score', 'is', null)
      .order('score', { ascending: true })

    if (start) q = q.gte('created_at', start)
    const { data } = await q
    const rows = data || []

    // Best gross score per user
    const bestByUser = {}
    rows.forEach(r => {
      if (!bestByUser[r.user_id] || r.score < bestByUser[r.user_id].score) {
        bestByUser[r.user_id] = r
      }
    })
    const sorted = Object.values(bestByUser).sort((a, b) => a.score - b.score)
    setCourseRows(sorted)
    setCourseLoading(false)
  }

  // ── Data: Following ────────────────────────────────

  async function fetchFollowing() {
    setFollowingLoading(true)
    const { data: followRows } = await supabase
      .from('follows')
      .select('profiles!follows_following_id_fkey(id, username, avatar_emoji, avatar_url)')
      .eq('follower_id', userId)
    const profiles = (followRows || []).map(r => r.profiles).filter(Boolean)
    setFollowing(profiles)
    if (profiles.length > 0) {
      await fetchFollowingStats(profiles)
      await fetchH2hOpponents(profiles)
    }
    setFollowingLoading(false)
  }

  async function fetchFollowingStats(followList) {
    const list = followList || following
    if (!list.length) return
    const ids = list.map(p => p.id)
    const start = periodStart(followingPeriod)

    let q = supabase
      .from('rounds')
      .select('user_id, score, hole_scores, created_at')
      .in('user_id', ids)
      .not('score', 'is', null)

    if (start) q = q.gte('created_at', start)
    const { data: rows } = await q

    const profileMap = {}
    list.forEach(p => { profileMap[p.id] = p })

    // Holes played per user
    const holesMap = {}
    // Best score per user
    const bestMap = {}

    ;(rows || []).forEach(r => {
      // Count holes from hole_scores array (non-null entries)
      const h = Array.isArray(r.hole_scores)
        ? r.hole_scores.filter(x => x != null).length
        : 0
      if (h > 0) holesMap[r.user_id] = (holesMap[r.user_id] || 0) + h

      if (!bestMap[r.user_id] || r.score < bestMap[r.user_id].score) {
        bestMap[r.user_id] = r
      }
    })

    const holesSorted = Object.entries(holesMap)
      .map(([uid, total]) => ({ ...profileMap[uid], userId: uid, total }))
      .sort((a, b) => b.total - a.total)

    const scoresSorted = Object.entries(bestMap)
      .map(([uid, r]) => ({ ...profileMap[uid], userId: uid, score: r.score, date: r.created_at }))
      .sort((a, b) => a.score - b.score)

    setHolesPlayed(holesSorted)
    setLowestScores(scoresSorted)
  }

  async function fetchH2hOpponents(followList) {
    const list = followList || following
    if (!list.length) return

    // Find competition opponents
    const { data: myCompEntries } = await supabase
      .from('competition_entries')
      .select('competition_id')
      .eq('user_id', userId)

    const compIds = [...new Set((myCompEntries || []).map(e => e.competition_id))]
    let compOpponentIds = []
    if (compIds.length > 0) {
      const { data: otherEntries } = await supabase
        .from('competition_entries')
        .select('user_id')
        .in('competition_id', compIds)
        .neq('user_id', userId)
        .not('user_id', 'is', null)
      compOpponentIds = [...new Set((otherEntries || []).map(e => e.user_id))]
    }

    // Find tournament opponents
    const { data: myMatchupEntries } = await supabase
      .from('tournament_matchup_entries')
      .select('matchup_id')
      .eq('user_id', userId)

    const matchupIds = [...new Set((myMatchupEntries || []).map(e => e.matchup_id))]
    let tournOpponentIds = []
    if (matchupIds.length > 0) {
      const { data: otherMatchupEntries } = await supabase
        .from('tournament_matchup_entries')
        .select('user_id')
        .in('matchup_id', matchupIds)
        .neq('user_id', userId)
        .not('user_id', 'is', null)
      tournOpponentIds = [...new Set((otherMatchupEntries || []).map(e => e.user_id))]
    }

    const opponentIdSet = new Set([...compOpponentIds, ...tournOpponentIds])
    // Only show opponents who are in the following list
    const opponents = list.filter(p => opponentIdSet.has(p.id))
    setH2hOpponents(opponents)
  }

  async function fetchH2H(opponentId) {
    setH2hLoading(true)
    let wins = 0, losses = 0, ties = 0
    const matches = []

    // ── Competition head-to-head ──
    // Fetch my entries and opponent entries separately — avoid broken embedded-join filters
    const [{ data: myCompEntries }, { data: oppCompEntries }] = await Promise.all([
      supabase.from('competition_entries').select('competition_id, score').eq('user_id', userId),
      supabase.from('competition_entries').select('competition_id, score').eq('user_id', opponentId)
    ])

    const myCompIds = new Set((myCompEntries || []).map(e => e.competition_id))
    const sharedCompIds = [...new Set((oppCompEntries || []).map(e => e.competition_id).filter(id => myCompIds.has(id)))]

    if (sharedCompIds.length > 0) {
      const { data: comps } = await supabase
        .from('competitions')
        .select('id, name, scoring_type, created_at')
        .in('id', sharedCompIds)

      const compMap = {}
      ;(comps || []).forEach(c => { compMap[c.id] = c })

      const myMap = {}
      ;(myCompEntries || []).forEach(e => { myMap[e.competition_id] = e })
      const oppMap = {}
      ;(oppCompEntries || []).forEach(e => { oppMap[e.competition_id] = e })

      sharedCompIds.forEach(cid => {
        const comp = compMap[cid]
        const me = myMap[cid]
        const opp = oppMap[cid]
        if (!comp || !me || !opp) return
        const myScore = me.score
        const oppScore = opp.score
        if (myScore == null || oppScore == null) return // skip unscored entries
        const lowWins = comp.scoring_type !== 'custom'
        let result = 'tie'
        if (myScore !== oppScore) {
          result = (lowWins ? myScore < oppScore : myScore > oppScore) ? 'win' : 'loss'
        }
        if (result === 'win') wins++
        else if (result === 'loss') losses++
        else ties++
        matches.push({ type: 'comp', name: comp.name, date: comp.created_at, myScore, oppScore, result })
      })
    }

    // ── Tournament matchup head-to-head ──
    const [{ data: myMuEntries }, { data: oppMuEntries }] = await Promise.all([
      supabase.from('tournament_matchup_entries').select('matchup_id, score').eq('user_id', userId),
      supabase.from('tournament_matchup_entries').select('matchup_id, score').eq('user_id', opponentId)
    ])

    const myMuIds = new Set((myMuEntries || []).map(e => e.matchup_id))
    const sharedMuIds = [...new Set((oppMuEntries || []).map(e => e.matchup_id).filter(id => myMuIds.has(id)))]

    if (sharedMuIds.length > 0) {
      const { data: matchups } = await supabase
        .from('tournament_matchups')
        .select('id, name, tournament_id')
        .in('id', sharedMuIds)

      const tournIds = [...new Set((matchups || []).map(m => m.tournament_id).filter(Boolean))]
      const tournMap = {}
      if (tournIds.length > 0) {
        const { data: tourns } = await supabase.from('tournaments').select('id, name').in('id', tournIds)
        ;(tourns || []).forEach(t => { tournMap[t.id] = t })
      }

      const muMap = {}
      ;(matchups || []).forEach(m => { muMap[m.id] = m })

      const myMuMap = {}
      ;(myMuEntries || []).forEach(e => { myMuMap[e.matchup_id] = e })
      const oppMuMap = {}
      ;(oppMuEntries || []).forEach(e => { oppMuMap[e.matchup_id] = e })

      sharedMuIds.forEach(mid => {
        const mu = muMap[mid]
        const me = myMuMap[mid]
        const opp = oppMuMap[mid]
        if (!mu || !me || !opp) return
        const myScore = me.score
        const oppScore = opp.score
        if (myScore == null || oppScore == null) return // skip unscored matchups
        let result = 'tie'
        if (myScore !== oppScore) {
          result = myScore < oppScore ? 'win' : 'loss' // lower score wins in golf
        }
        if (result === 'win') wins++
        else if (result === 'loss') losses++
        else ties++
        const tournName = mu.tournament_id ? (tournMap[mu.tournament_id]?.name || 'Tournament') : 'Tournament'
        matches.push({ type: 'tourney', name: `${tournName} — ${mu.name}`, date: null, myScore, oppScore, result })
      })
    }

    // Sort: competitions with dates first, tournament matchups after
    matches.sort((a, b) => (b.date || '') > (a.date || '') ? 1 : -1)
    setH2hRecord({ wins, losses, ties, matches })
    setH2hLoading(false)
  }

  // ── Derived ────────────────────────────────────────

  const myCourseBestIdx = courseRows.findIndex(r => r.user_id === userId)
  const myCourseBest = myCourseBestIdx >= 0 ? courseRows[myCourseBestIdx] : null
  const topCourse = courseRows.slice(0, 10)

  // ══════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════

  return (
    <div style={{ ...st.page, paddingBottom: isMobile ? 72 : 0 }}>

      {/* ── Topbar ── */}
      <div style={st.topbar}>
        <div style={st.topbarLeft}>
          <div style={st.logo}>BREAKIE</div>
        </div>
        <div style={st.topbarRight}>
          <Link to="/log" style={st.postBtn}>+ Log Round</Link>
          {myProfile && (
            <div style={st.avatarWrap}><AvatarImg profile={myProfile} /></div>
          )}
        </div>
      </div>

      <Nav />

      {/* ── Main Tab Toggle ── */}
      <div style={st.mainTabs}>
        <button style={{ ...st.mainTab, ...(mainTab === 'course' ? st.mainTabActive : {}) }}
          onClick={() => setMainTab('course')}>⛳ Course Boards</button>
        <button style={{ ...st.mainTab, ...(mainTab === 'following' ? st.mainTabActive : {}) }}
          onClick={() => setMainTab('following')}>👥 Following</button>
      </div>

      <div style={st.body}>

        {/* ══════════════════════════════════════════
            COURSE BOARDS
        ══════════════════════════════════════════ */}
        {mainTab === 'course' && (
          <div>
            {/* Course dropdown */}
            <div style={st.courseSelectWrap}>
              <div style={st.courseSelectLabel}>SELECT COURSE</div>
              {courses.length === 0 ? (
                <div style={{ color: '#6b7c6e', fontSize: 13, padding: '8px 0' }}>No courses logged yet.</div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <select
                    style={st.courseSelect}
                    value={selectedCourse}
                    onChange={e => setSelectedCourse(e.target.value)}
                  >
                    {courses.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <div style={st.selectArrow}>▾</div>
                </div>
              )}
              {selectedCourse && (
                <Link
                  to={`/course?name=${encodeURIComponent(selectedCourse)}`}
                  style={st.courseViewAll}
                >
                  View full course board →
                </Link>
              )}
            </div>

            {/* Period tabs */}
            {selectedCourse && (
              <div style={st.periodTabs}>
                {COURSE_PERIODS.map(p => (
                  <button key={p.key}
                    style={{ ...st.periodTab, ...(coursePeriod === p.key ? st.periodTabActive : {}) }}
                    onClick={() => setCoursePeriod(p.key)}>{p.label}</button>
                ))}
              </div>
            )}

            {/* Leaderboard */}
            {selectedCourse && (
              <div style={st.section}>
                <div style={st.sectionHeader}>
                  <div style={st.sectionTitle}>TOP 10</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      style={{ ...st.scoreModeBtn, ...(scoreMode === 'gross' ? st.scoreModeBtnActive : {}) }}
                      onClick={() => setScoreMode('gross')}
                    >Gross</button>
                    <button
                      style={{ ...st.scoreModeBtn, ...(scoreMode === 'net' ? st.scoreModeBtnActive : {}) }}
                      onClick={() => setScoreMode('net')}
                    >Net</button>
                  </div>
                </div>

                {courseLoading && <div style={st.empty}>Loading...</div>}

                {!courseLoading && topCourse.length === 0 && (
                  <div style={st.empty}>No scores logged {coursePeriod === 'daily' ? 'today' : `this ${coursePeriod === 'alltime' ? 'season' : coursePeriod}`}.</div>
                )}

                {!courseLoading && (() => {
                  // For net mode, filter to rows that have net_score, sort by net; fall back to gross
                  const displayRows = scoreMode === 'net'
                    ? [...courseRows]
                        .filter(r => r.net_score != null)
                        .sort((a, b) => a.net_score - b.net_score)
                        .slice(0, 10)
                    : topCourse

                  if (displayRows.length === 0 && scoreMode === 'net') {
                    return <div style={st.empty}>No net scores yet — log rounds with your handicap index to see net rankings.</div>
                  }

                  const medals = ['🥇', '🥈', '🥉']
                  return (
                    <>
                      {displayRows.map((row, i) => {
                        const isMe = row.user_id === userId
                        const displayScore = scoreMode === 'net' ? row.net_score : row.score
                        const diff = row.par ? row.score - row.par : null
                        const netDiff = row.par && row.net_score != null ? row.net_score - row.par : null
                        return (
                          <div key={row.id} style={{
                            ...st.lbRow,
                            background: isMe ? 'rgba(200,241,53,0.07)' : i % 2 === 0 ? 'white' : '#fafcfb',
                            borderLeft: isMe ? '3px solid #c8f135' : '3px solid transparent'
                          }}>
                            <div style={st.lbRank}>
                              {i < 3 ? <span style={{ fontSize: 18 }}>{medals[i]}</span>
                                : <span style={st.rankNum}>{i + 1}</span>}
                            </div>
                            <div style={st.lbAvatar}><AvatarImg profile={row.profiles} /></div>
                            <div style={st.lbInfo}>
                              <div style={st.lbName}>
                                @{row.profiles?.username || 'golfer'}
                                {isMe && <span style={st.youTag}> you</span>}
                              </div>
                              <div style={st.lbDate}>{fmtDate(row.created_at)}</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                              <div style={{
                                ...st.scoreBadge,
                                background: diff == null ? '#1a7a3c' : diff < 0 ? '#1a7a3c' : diff === 0 ? '#2980b9' : '#c0392b'
                              }}>
                                <div style={st.scoreBadgeNum}>{displayScore}</div>
                                {scoreMode === 'gross' && diff != null && <div style={st.scoreBadgeDiff}>{diff === 0 ? 'E' : diff > 0 ? `+${diff}` : diff}</div>}
                                {scoreMode === 'net' && netDiff != null && <div style={st.scoreBadgeDiff}>{netDiff === 0 ? 'E' : netDiff > 0 ? `+${netDiff}` : netDiff}</div>}
                              </div>
                              {scoreMode === 'net' && row.score != null && (
                                <div style={st.grossSubLabel}>Gross {row.score}</div>
                              )}
                              {scoreMode === 'gross' && row.net_score != null && (
                                <div style={st.netSubLabel}>Net {row.net_score}</div>
                              )}
                            </div>
                          </div>
                        )
                      })}

                      {/* User's own rank if outside top 10 — gross mode only */}
                      {scoreMode === 'gross' && myCourseBest && myCourseBestIdx >= 10 && (
                        <>
                          <div style={st.lbDivider}>···</div>
                          <div style={{ ...st.lbRow, background: 'rgba(200,241,53,0.07)', borderLeft: '3px solid #c8f135' }}>
                            <div style={st.lbRank}><span style={st.rankNum}>{myCourseBestIdx + 1}</span></div>
                            <div style={st.lbAvatar}><AvatarImg profile={myCourseBest.profiles} /></div>
                            <div style={st.lbInfo}>
                              <div style={st.lbName}>@{myCourseBest.profiles?.username || 'you'}<span style={st.youTag}> you</span></div>
                              <div style={st.lbDate}>{fmtDate(myCourseBest.created_at)}</div>
                            </div>
                            <div style={{ ...st.scoreBadge, background: '#1a7a3c' }}>
                              <div style={st.scoreBadgeNum}>{myCourseBest.score}</div>
                            </div>
                          </div>
                        </>
                      )}

                      {!myCourseBest && courseRows.length > 0 && (
                        <div style={{ ...st.empty, borderTop: '1px solid #f0f4f1', fontSize: 12, color: '#9aaa9d' }}>
                          You haven't logged a round here {coursePeriod !== 'alltime' ? 'this period' : 'yet'}.
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════
            FOLLOWING BOARDS
        ══════════════════════════════════════════ */}
        {mainTab === 'following' && (
          <div>
            {followingLoading && <div style={st.empty}>Loading...</div>}

            {!followingLoading && following.length === 0 && (
              <div style={st.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1f13', marginBottom: 6 }}>Not following anyone yet</div>
                <div style={{ fontSize: 13, color: '#6b7c6e' }}>Follow other players from their profiles to see stats here.</div>
              </div>
            )}

            {!followingLoading && following.length > 0 && (
              <>
                {/* Period tabs */}
                <div style={st.periodTabs}>
                  {FOLLOWING_PERIODS.map(p => (
                    <button key={p.key}
                      style={{ ...st.periodTab, ...(followingPeriod === p.key ? st.periodTabActive : {}) }}
                      onClick={() => setFollowingPeriod(p.key)}>{p.label}</button>
                  ))}
                </div>

                {/* Holes Played */}
                <div style={st.section}>
                  <div style={st.sectionHeader}>
                    <div style={st.sectionTitle}>⛳ HOLES PLAYED</div>
                    <div style={st.sectionSub}>{FOLLOWING_PERIODS.find(p => p.key === followingPeriod)?.label}</div>
                  </div>
                  {holesPlayed.length === 0 ? (
                    <div style={st.empty}>No rounds logged this period.</div>
                  ) : (
                    holesPlayed.map((row, i) => (
                      <div key={row.id || row.userId} style={{
                        ...st.lbRow,
                        background: i % 2 === 0 ? 'white' : '#fafcfb',
                        borderLeft: i === 0 ? '3px solid #c8f135' : '3px solid transparent'
                      }}>
                        <div style={st.lbRank}>
                          {i < 3
                            ? <span style={{ fontSize: 18 }}>{'🥇🥈🥉'[i]}</span>
                            : <span style={st.rankNum}>{i + 1}</span>}
                        </div>
                        <div style={st.lbAvatar}><AvatarImg profile={row} /></div>
                        <div style={st.lbInfo}>
                          <div style={st.lbName}>@{row.username || 'player'}</div>
                        </div>
                        <div style={st.statBadge}>
                          <div style={st.statNum}>{row.total}</div>
                          <div style={st.statLabel}>holes</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Lowest Scores */}
                <div style={st.section}>
                  <div style={st.sectionHeader}>
                    <div style={st.sectionTitle}>🏆 LOWEST SCORES</div>
                    <div style={st.sectionSub}>{FOLLOWING_PERIODS.find(p => p.key === followingPeriod)?.label}</div>
                  </div>
                  {lowestScores.length === 0 ? (
                    <div style={st.empty}>No scores this period.</div>
                  ) : (
                    lowestScores.map((row, i) => (
                      <div key={row.id || row.userId} style={{
                        ...st.lbRow,
                        background: i % 2 === 0 ? 'white' : '#fafcfb',
                        borderLeft: i === 0 ? '3px solid #c8f135' : '3px solid transparent'
                      }}>
                        <div style={st.lbRank}>
                          {i < 3
                            ? <span style={{ fontSize: 18 }}>{'🥇🥈🥉'[i]}</span>
                            : <span style={st.rankNum}>{i + 1}</span>}
                        </div>
                        <div style={st.lbAvatar}><AvatarImg profile={row} /></div>
                        <div style={st.lbInfo}>
                          <div style={st.lbName}>@{row.username || 'player'}</div>
                          <div style={st.lbDate}>{fmtDate(row.date)}</div>
                        </div>
                        <div style={{ ...st.scoreBadge, background: '#1a7a3c' }}>
                          <div style={st.scoreBadgeNum}>{row.score}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Head-to-Head */}
                <div style={st.section}>
                  <div style={st.sectionHeader}>
                    <div style={st.sectionTitle}>⚔️ HEAD-TO-HEAD</div>
                    <div style={st.sectionSub}>All-Time</div>
                  </div>

                  <div style={{ padding: '12px 16px 4px' }}>
                    <div style={st.courseSelectLabel}>SELECT OPPONENT</div>
                    {h2hOpponents.length === 0 ? (
                      <div style={{ color: '#6b7c6e', fontSize: 13, padding: '8px 0' }}>
                        No head-to-head data yet. Compete or play tournaments with people you follow.
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <select
                          style={st.courseSelect}
                          value={selectedOpponent?.id || ''}
                          onChange={e => {
                            const opp = h2hOpponents.find(p => p.id === e.target.value)
                            setSelectedOpponent(opp || null)
                            setH2hRecord(null)
                          }}
                        >
                          <option value="">Choose opponent...</option>
                          {h2hOpponents.map(p => (
                            <option key={p.id} value={p.id}>@{p.username}</option>
                          ))}
                        </select>
                        <div style={st.selectArrow}>▾</div>
                      </div>
                    )}
                  </div>

                  {h2hLoading && <div style={st.empty}>Loading...</div>}

                  {!h2hLoading && h2hRecord && selectedOpponent && (
                    <>
                      {/* Record banner */}
                      <div style={st.h2hBanner}>
                        <div style={st.h2hBlock}>
                          <div style={{ ...st.h2hNum, color: '#c8f135' }}>{h2hRecord.wins}</div>
                          <div style={st.h2hLabel}>Wins</div>
                        </div>
                        <div style={{ ...st.h2hBlock, borderLeft: '1px solid rgba(255,255,255,0.1)', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                          <div style={st.h2hNum}>{h2hRecord.ties}</div>
                          <div style={st.h2hLabel}>Ties</div>
                        </div>
                        <div style={st.h2hBlock}>
                          <div style={{ ...st.h2hNum, color: '#e74c3c' }}>{h2hRecord.losses}</div>
                          <div style={st.h2hLabel}>Losses</div>
                        </div>
                      </div>

                      {/* Match history */}
                      {h2hRecord.matches.length === 0 && (
                        <div style={st.empty}>No direct matchups found.</div>
                      )}
                      {h2hRecord.matches.length > 0 && (() => {
                        const tourneyMatches = h2hRecord.matches.filter(m => m.type === 'tourney')
                        const compMatches = h2hRecord.matches.filter(m => m.type === 'comp')

                        const renderMatch = (m, i) => (
                          <div key={i} style={{
                            ...st.h2hMatchRow,
                            background: i % 2 === 0 ? 'white' : '#fafcfb'
                          }}>
                            <div style={{
                              ...st.h2hResultBadge,
                              background: m.result === 'win' ? '#e8f5ed' : m.result === 'loss' ? '#fde8e8' : '#f4f7f5',
                              color: m.result === 'win' ? '#1a7a3c' : m.result === 'loss' ? '#c0392b' : '#6b7c6e'
                            }}>
                              {m.result === 'win' ? 'W' : m.result === 'loss' ? 'L' : 'T'}
                            </div>
                            <div style={st.h2hMatchInfo}>
                              <div style={st.h2hMatchName}>{m.name}</div>
                              {m.date && <div style={st.h2hMatchDate}>{fmtDate(m.date)}</div>}
                            </div>
                            {m.myScore != null && m.oppScore != null && (
                              <div style={st.h2hScores}>
                                <span style={{ fontWeight: 800, color: '#0e1f13' }}>{m.myScore}</span>
                                <span style={{ color: '#9aaa9d', margin: '0 4px' }}>vs</span>
                                <span style={{ color: '#6b7c6e' }}>{m.oppScore}</span>
                              </div>
                            )}
                          </div>
                        )

                        return (
                          <>
                            {tourneyMatches.length > 0 && (
                              <>
                                <div style={st.h2hSectionLabel}>Tournament Matchups</div>
                                {tourneyMatches.map((m, i) => renderMatch(m, i))}
                              </>
                            )}
                            {compMatches.length > 0 && (
                              <>
                                <div style={st.h2hSectionLabel}>Regular Matchups</div>
                                {compMatches.map((m, i) => renderMatch(m, i))}
                              </>
                            )}
                          </>
                        )
                      })()}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────
const st = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },

  // ── Topbar ────────────────────────────────────────
  topbar: {
    background: '#0e1f13', padding: '12px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  topbarLeft: { display: 'flex', alignItems: 'center' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 10 },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: '#c8f135', letterSpacing: 2
  },
  postBtn: {
    background: '#c8f135', color: '#0e1f13', textDecoration: 'none',
    borderRadius: 20, padding: '7px 14px', fontWeight: 800, fontSize: 12,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  avatarWrap: { width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer' },


  // ── Main tabs ─────────────────────────────────────
  mainTabs: {
    display: 'flex', background: 'white',
    borderBottom: '2px solid #f0f4f1', padding: '0 16px', gap: 4
  },
  mainTab: {
    flex: 1, background: 'none', border: 'none', borderBottom: '3px solid transparent',
    padding: '14px 8px', fontSize: 13, fontWeight: 700, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5,
    marginBottom: -2
  },
  mainTabActive: { color: '#0e1f13', borderBottomColor: '#c8f135' },

  body: { maxWidth: 640, margin: '0 auto', padding: '16px 16px 48px' },

  // ── Course selector ───────────────────────────────
  courseSelectWrap: { marginBottom: 16 },
  courseSelectLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
    textTransform: 'uppercase', color: '#6b7c6e', marginBottom: 6
  },
  courseSelect: {
    width: '100%', background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 12, padding: '12px 40px 12px 14px', fontSize: 14,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif",
    fontWeight: 600, cursor: 'pointer', appearance: 'none', boxSizing: 'border-box'
  },
  selectArrow: {
    position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
    color: '#6b7c6e', fontSize: 14, pointerEvents: 'none'
  },
  courseViewAll: {
    display: 'inline-block', marginTop: 8,
    color: '#1a7a3c', fontSize: 12, fontWeight: 700, textDecoration: 'none'
  },

  // ── Period tabs ───────────────────────────────────
  periodTabs: {
    display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto'
  },
  periodTab: {
    background: 'white', border: '1.5px solid #e2ebe4', borderRadius: 20,
    padding: '7px 16px', fontSize: 12, fontWeight: 700, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif", whiteSpace: 'nowrap',
    flexShrink: 0
  },
  periodTabActive: { background: '#0e1f13', borderColor: '#0e1f13', color: '#c8f135' },

  // ── Section ───────────────────────────────────────
  section: {
    background: 'white', borderRadius: 18, overflow: 'hidden',
    marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    padding: '14px 16px 10px', borderBottom: '1px solid #f0f4f1'
  },
  sectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 13, color: '#0e1f13', letterSpacing: 1.5, textTransform: 'uppercase'
  },
  sectionSub: { fontSize: 11, color: '#9aaa9d', fontWeight: 600 },

  empty: { textAlign: 'center', padding: '24px 16px', color: '#9aaa9d', fontSize: 13 },
  emptyState: { padding: '48px 20px', textAlign: 'center' },

  // ── Leaderboard row ───────────────────────────────
  lbRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', borderBottom: '1px solid #f8faf8'
  },
  lbRank: { width: 28, flexShrink: 0, textAlign: 'center' },
  rankNum: {
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 14, color: '#9aaa9d'
  },
  lbAvatar: {
    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
  },
  lbInfo: { flex: 1, minWidth: 0 },
  lbName: { fontWeight: 700, fontSize: 13, color: '#0e1f13' },
  lbDate: { fontSize: 10, color: '#9aaa9d', marginTop: 1 },
  youTag: { color: '#1a7a3c', fontSize: 10, fontWeight: 700 },

  lbDivider: {
    textAlign: 'center', padding: '6px 0', color: '#c8d5ca',
    fontSize: 13, letterSpacing: 4, background: '#fafcfb'
  },

  // Score badge
  scoreBadge: {
    borderRadius: 10, padding: '6px 10px', textAlign: 'center',
    flexShrink: 0, minWidth: 48
  },
  scoreBadgeNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 20, color: 'white', lineHeight: 1
  },
  scoreBadgeDiff: { fontSize: 9, color: 'rgba(255,255,255,0.7)', marginTop: 2 },

  // Gross/Net toggle
  scoreModeBtn: {
    background: 'white', border: '1.5px solid #e2ebe4', borderRadius: 20,
    padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  scoreModeBtnActive: { background: '#0e1f13', borderColor: '#0e1f13', color: '#c8f135' },
  netSubLabel: {
    fontSize: 9, color: '#1a7a3c', fontWeight: 700, letterSpacing: 0.3
  },
  grossSubLabel: {
    fontSize: 9, color: '#6b7c6e', fontWeight: 600, letterSpacing: 0.3
  },

  // Stat badge (holes played)
  statBadge: {
    background: '#f4f7f5', borderRadius: 10, padding: '6px 12px',
    textAlign: 'center', flexShrink: 0, minWidth: 56
  },
  statNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 20, color: '#0e1f13', lineHeight: 1
  },
  statLabel: { fontSize: 9, color: '#9aaa9d', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.8 },

  // ── Head-to-Head ──────────────────────────────────
  h2hBanner: {
    background: '#0e1f13', display: 'flex', padding: '16px 0', margin: '0'
  },
  h2hBlock: { flex: 1, textAlign: 'center', padding: '0 12px' },
  h2hNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 36, color: 'white', lineHeight: 1
  },
  h2hLabel: { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 },

  h2hMatchRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', borderBottom: '1px solid #f8faf8'
  },
  h2hResultBadge: {
    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 14
  },
  h2hMatchInfo: { flex: 1, minWidth: 0 },
  h2hMatchName: {
    fontWeight: 700, fontSize: 12, color: '#0e1f13',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  h2hMatchDate: { fontSize: 10, color: '#9aaa9d', marginTop: 1 },
  h2hScores: { fontSize: 12, fontWeight: 600, color: '#6b7c6e', flexShrink: 0 },

  h2hSectionLabel: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 11, letterSpacing: 1.5,
    textTransform: 'uppercase', color: '#9aaa9d',
    padding: '10px 16px 6px', background: '#f8faf8',
    borderBottom: '1px solid #f0f4f1', borderTop: '1px solid #f0f4f1'
  },
}
